// Recurrence for quick capture ("boxing every tuesday and thurs").
//
// The grammar lives in parseRoutine — this file only decides WHEN a quick-add
// line is talking about recurrence at all, and turns the result into the
// three things a capture surface needs: the RecurrencePattern, the first
// occurrence, and an RRULE for a calendar series.
//
// The gate matters more than the grammar. parseRoutine will happily read a
// bare "tuesday" as a weekly routine, but in quick capture a bare weekday is
// a one-off date ("text Karen tuesday") — a real reported data-loss shape.
// Only an explicit cue ("every …", "daily", "on tuesdays") opens the door.

import { parseRoutine, parsedRoutineToDb } from './parseRoutine'
import type { RecurrencePattern } from '@/types/actionable'
import { hasMonthlyPosition, nextMonthlyPositionWindow, describeMonthlyPosition } from './cadence/monthlyPosition'

export interface DetectedRecurrence {
  pattern: RecurrencePattern
  /** HH:MM when the phrase carried a clock time, else null. */
  time: string | null
  /** From a time range ("from 9am to 10:15am", "7-8am"); undefined otherwise. */
  durationMinutes?: number
  /** The recurrence phrase as typed, for the preview chip and for tests. */
  match: string
  /** The line with the recurrence phrase and any time removed. */
  rest: string
}

const DAY = '(?:sun|mon|tues?|wed(?:nes)?|thurs?|fri|sat(?:ur)?)'
const DAY_LIST = `${DAY}(?:day)?s?(?:\\s*(?:,|and|&)\\s*${DAY}(?:day)?s?)*`
const UNIT = '(?:day|days|weekday|weekdays|weekend|weekends|week|weeks|month|months|quarter|quarters|year|years|morning|afternoon|evening|night)'

// "every <unit|day list>", optionally "every other" / "every 2" / "every two",
// with a monthly date tail ("every month on the 15th").
const EVERY_RE = new RegExp(
  `\\bevery\\s+(?:other\\s+|\\d+\\s+|two\\s+|three\\s+)?(?:${UNIT}|${DAY_LIST})\\b(?:\\s+on\\s+the\\s+\\d{1,2}(?:st|nd|rd|th)?\\b)?`,
  'i',
)
// Bare adverbs.
const ADVERB_RE = /\b(daily|weekly|monthly|quarterly|yearly|weekdays|weekends)\b/i
// "…the first weekend of each month" — "of each month" can only mean a rule.
// ("of the month" stays out: "dentist the first friday of the month" is a date.)
const EACH_MONTH_RE = /\b(?:first|1st|second|2nd|third|3rd|fourth|4th|last)\s+\w+\s+(?:of|in)\s+each\s+month\b/i
// Plural weekdays, with or without "on": "tuesdays", "on tuesdays and thursdays".
const PLURAL_DAYS_RE = new RegExp(`\\b(?:on\\s+)?${DAY}days(?:\\s*(?:,|and|&)\\s*${DAY}days)*\\b`, 'i')

const ADVERB_TO_PHRASE: Record<string, string> = {
  daily: 'every day',
  weekly: 'every week',
  monthly: 'every month',
  quarterly: 'every quarter',
  yearly: 'every year',
  weekdays: 'every weekday',
  weekends: 'every weekend',
}

// A clock range: "from 9am to 10:15am", "7-8am", "11:30 to 1pm", "4–5:30pm".
// At least one meridiem is required so "2 to 3 chapters" stays words.
const RANGE_RE = /\b(?:from\s+)?(\d{1,2})(?::(\d{2}))?\s*(am?|pm?)?\s*(?:-|–|—|to|until|till)\s*(\d{1,2})(?::(\d{2}))?\s*(am?|pm?)?\b/i

interface ClockRange { time: string; durationMinutes?: number; match: string }

function to24(hour12: number, minutes: number, meridiem: string): number | null {
  if (hour12 < 1 || hour12 > 12 || minutes > 59) return null
  const pm = meridiem.toLowerCase().startsWith('p')
  const h = pm ? (hour12 === 12 ? 12 : hour12 + 12) : hour12 === 12 ? 0 : hour12
  return h * 60 + minutes
}

function hhmm(totalMinutes: number): string {
  return `${String(Math.floor(totalMinutes / 60)).padStart(2, '0')}:${String(totalMinutes % 60).padStart(2, '0')}`
}

function readClockRange(input: string): ClockRange | null {
  const m = input.match(RANGE_RE)
  if (!m || (!m[3] && !m[6])) return null
  const startHour = Number(m[1]), startMin = Number(m[2] ?? 0)
  const endHour = Number(m[4]), endMin = Number(m[5] ?? 0)
  const endMer = m[6] ?? m[3]!
  // "11:30 to 1pm": a start with no meridiem borrows the end's, unless that
  // would put it after the end — then it's the other half of the day.
  let startMer = m[3] ?? endMer
  if (!m[3] && startHour > endHour) startMer = endMer.toLowerCase().startsWith('p') ? 'am' : 'pm'
  const start = to24(startHour, startMin, startMer)
  const end = to24(endHour, endMin, endMer)
  if (start == null) return null
  return {
    time: hhmm(start),
    durationMinutes: end != null && end > start ? end - start : undefined,
    match: m[0],
  }
}

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const RRULE_DAYS: Record<string, string> = { sun: 'SU', mon: 'MO', tue: 'TU', wed: 'WE', thu: 'TH', fri: 'FR', sat: 'SA' }

export function detectRecurrence(input: string, now: Date = new Date()): DetectedRecurrence | null {
  const every = input.match(EVERY_RE)
  const adverb = every ? null : input.match(ADVERB_RE)
  const eachMonth = every || adverb ? null : input.match(EACH_MONTH_RE)
  const plural = every || adverb || eachMonth ? null : input.match(PLURAL_DAYS_RE)
  const cue = every ?? adverb ?? eachMonth ?? plural
  if (!cue) return null

  // A range goes first: parseRoutine reads one clock time, and its patterns
  // are ordered by shape, not position — "from 9am to 10:15am" would come
  // back as 10:15 with "from 9am to" still in the title.
  const range = readClockRange(input)
  let text = range ? input.replace(range.match, ' ') : input
  // parseRoutine speaks "every …"; hand it the adverb spelled out.
  if (adverb) text = text.replace(ADVERB_RE, (w) => ADVERB_TO_PHRASE[w.toLowerCase()])
  const parsed = parseRoutine(text)
  let pattern = parsedRoutineToDb(parsed).recurrence_pattern as RecurrencePattern

  // "every week" / "weekly" names no day — parseRoutine falls back to daily.
  // A weekly thing with no day named happens on the day it was captured.
  if (/^every\s+weeks?$/i.test(cue[0]) || /^weekly$/i.test(cue[0])) {
    pattern = { type: 'weekly', days: [DAY_KEYS[now.getDay()]] }
  }

  return {
    pattern,
    time: range?.time ?? parsed.time,
    durationMinutes: range?.durationMinutes,
    match: cue[0].trim(),
    // The phrase is gone but the preposition that led into it often isn't.
    rest: parsed.action
      .replace(/\s+/g, ' ')
      .replace(/^(?:from|at|on|by|to|until|till)\s+/i, '')
      .replace(/\s+(?:from|at|on|by|to|until|till)$/i, '')
      .trim(),
  }
}

/** Google Calendar `recurrence` lines, or null when the pattern has no RRULE shape. */
export function recurrenceToRRule(pattern: RecurrencePattern): string[] | null {
  const interval = pattern.interval && pattern.interval > 1 ? `;INTERVAL=${pattern.interval}` : ''
  switch (pattern.type) {
    case 'daily':
      return [`RRULE:FREQ=DAILY${interval}`]
    case 'weekly': {
      const days = (pattern.days ?? []).map((d) => RRULE_DAYS[d]).filter(Boolean)
      const byDay = days.length ? `;BYDAY=${days.join(',')}` : ''
      return [`RRULE:FREQ=WEEKLY${interval}${byDay}`]
    }
    case 'monthly': {
      // A calendar has no window: a weekend position is its Saturday.
      if (hasMonthlyPosition(pattern)) {
        const day = pattern.day_of_week === 'weekend' ? 'SA' : RRULE_DAYS[pattern.day_of_week]
        return [`RRULE:FREQ=MONTHLY${interval};BYDAY=${pattern.week_of_month}${day}`]
      }
      const byMonthDay = pattern.day_of_month ? `;BYMONTHDAY=${pattern.day_of_month}` : ''
      return [`RRULE:FREQ=MONTHLY${interval}${byMonthDay}`]
    }
    case 'quarterly':
      return ['RRULE:FREQ=MONTHLY;INTERVAL=3']
    case 'yearly':
      return [`RRULE:FREQ=YEARLY${interval}`]
    // A calendar has no window; a weekend series is Saturday and Sunday.
    case 'weekend':
      return ['RRULE:FREQ=WEEKLY;BYDAY=SA,SU']
    default:
      return null
  }
}

function atTime(day: Date, time: string | null): Date {
  const d = new Date(day)
  if (time) {
    const [h, m] = time.split(':').map(Number)
    d.setHours(h, m, 0, 0)
  } else {
    d.setHours(0, 0, 0, 0)
  }
  return d
}

/**
 * The first day the pattern lands on, counting today — unless today's clock
 * time is already behind us, in which case the next one.
 */
export function nextOccurrence(pattern: RecurrencePattern, time: string | null, now: Date = new Date()): Date {
  const today = new Date(now)
  today.setHours(0, 0, 0, 0)
  const stillAhead = (candidate: Date) => !time || candidate.getTime() > now.getTime()

  if (pattern.type === 'weekly' && pattern.days?.length) {
    const wanted = new Set(pattern.days.map((d) => DAY_KEYS.indexOf(d as typeof DAY_KEYS[number])))
    for (let offset = 0; offset < 8; offset++) {
      const day = new Date(today)
      day.setDate(today.getDate() + offset)
      if (!wanted.has(day.getDay())) continue
      const candidate = atTime(day, time)
      if (offset > 0 || stillAhead(candidate)) return candidate
    }
  }

  if (pattern.type === 'weekend') {
    for (let offset = 0; offset < 8; offset++) {
      const day = new Date(today)
      day.setDate(today.getDate() + offset)
      if (day.getDay() !== 0 && day.getDay() !== 6) continue
      const candidate = atTime(day, time)
      if (offset > 0 || stillAhead(candidate)) return candidate
    }
  }

  if (hasMonthlyPosition(pattern)) {
    // The first day of the next window still ahead — or today, inside one.
    let window = nextMonthlyPositionWindow(pattern, today)
    let first = window.find((d) => d.getTime() >= today.getTime())!
    if (first.getTime() === today.getTime() && !stillAhead(atTime(first, time))) {
      const rest = window.filter((d) => d.getTime() > today.getTime())
      if (rest.length === 0) {
        window = nextMonthlyPositionWindow(pattern, new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1))
        first = window[0]
      } else {
        first = rest[0]
      }
    }
    return atTime(first, time)
  }

  if (pattern.type === 'monthly' && pattern.day_of_month) {
    for (let months = 0; months < 2; months++) {
      const day = new Date(today.getFullYear(), today.getMonth() + months, pattern.day_of_month)
      if (day.getTime() < today.getTime()) continue
      const candidate = atTime(day, time)
      if (day.getTime() > today.getTime() || stillAhead(candidate)) return candidate
    }
  }

  if (pattern.type === 'yearly' && pattern.month_of_year) {
    const day = pattern.day_of_month ?? 1
    for (let years = 0; years < 2; years++) {
      const at = new Date(today.getFullYear() + years, pattern.month_of_year - 1, day)
      if (at.getTime() < today.getTime()) continue
      const candidate = atTime(at, time)
      if (at.getTime() > today.getTime() || stillAhead(candidate)) return candidate
    }
  }

  if (pattern.type === 'quarterly') {
    // Anchored on the pattern's own start month when it has one, so "every
    // quarter from February" lands Feb/May/Aug/Nov rather than on the calendar
    // quarters.
    const anchor = pattern.start_date ? new Date(`${pattern.start_date}T00:00:00`) : null
    const anchorMonth = anchor && !Number.isNaN(anchor.getTime()) ? anchor.getMonth() : 0
    // The anchor carries the DAY as well as the month: "every quarter from
    // Jan 15" means the 15th, not the 1st.
    const day = pattern.day_of_month ?? (anchor && !Number.isNaN(anchor.getTime()) ? anchor.getDate() : 1)
    for (let step = 0; step < 5; step++) {
      const at = new Date(today.getFullYear(), anchorMonth + step * 3, day)
      if (at.getTime() < today.getTime()) continue
      const candidate = atTime(at, time)
      if (at.getTime() > today.getTime() || stillAhead(candidate)) return candidate
    }
    // Past the last anchor this year — wrap to the first one next year.
    return atTime(new Date(today.getFullYear() + 1, anchorMonth, day), time)
  }

  if (pattern.type === 'specific_days' && pattern.dates?.length) {
    const ahead = pattern.dates
      .map((d) => new Date(`${d}T00:00:00`))
      .filter((d) => !Number.isNaN(d.getTime()) && d.getTime() >= today.getTime())
      .sort((a, b) => a.getTime() - b.getTime())
    if (ahead.length > 0) return atTime(ahead[0], time)
  }

  const todayCandidate = atTime(today, time)
  if (stillAhead(todayCandidate)) return todayCandidate
  const tomorrow = new Date(today)
  tomorrow.setDate(today.getDate() + 1)
  return atTime(tomorrow, time)
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`
}

/** Short label for the preview chip: "Every Tue, Thu", "Every other Mon". */
export function describeRecurrence(pattern: RecurrencePattern): string {
  const every = pattern.interval === 2 ? 'Every other' : pattern.interval && pattern.interval > 2 ? `Every ${pattern.interval}` : 'Every'
  switch (pattern.type) {
    case 'daily':
      return pattern.interval && pattern.interval > 1 ? `${every} days` : 'Every day'
    case 'weekly': {
      const days = pattern.days ?? []
      const key = [...days].sort((a, b) => DAY_KEYS.indexOf(a as never) - DAY_KEYS.indexOf(b as never)).join(',')
      if (!pattern.interval || pattern.interval === 1) {
        if (key === 'mon,tue,wed,thu,fri') return 'Weekdays'
        // Both days, two commitments — not the Weekend rule's once (2026-10-03).
        if (key === 'sun,sat') return 'Sat and Sun'
        if (days.length === 7) return 'Every day'
      }
      if (days.length === 0) return pattern.interval && pattern.interval > 1 ? `${every} weeks` : 'Every week'
      const labels = key.split(',').map((d) => DAY_LABELS[DAY_KEYS.indexOf(d as never)])
      return `${every} ${labels.join(', ')}`
    }
    case 'monthly':
      if (hasMonthlyPosition(pattern)) return `Monthly, ${describeMonthlyPosition(pattern)}`
      return pattern.day_of_month ? `Monthly on the ${ordinal(pattern.day_of_month)}` : 'Every month'
    case 'quarterly':
      return 'Every quarter'
    case 'yearly':
      return pattern.interval && pattern.interval > 1 ? `${every} years` : 'Every year'
    case 'since_last': {
      // NOT "every 6 weeks": the clock starts when you last did it, which is a
      // different promise from a fixed schedule. Said plainly, because
      // 'Repeats' told the reader nothing at all.
      const n = pattern.interval && pattern.interval > 0 ? pattern.interval : 1
      const unit = pattern.unit ?? 'days'
      const noun = n === 1 ? unit.replace(/s$/, '') : unit
      return `${n} ${noun} after the last time`
    }
    case 'specific_days':
      return pattern.dates?.length ? `${pattern.dates.length} set dates` : 'On set dates'
    case 'weekend':
      return 'Weekends'
    default:
      return 'Repeats'
  }
}
